'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function MeanMedianModeRangeCalculator() {
  const t = useTranslations("tools.meanMedianModeRangeCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [numberInput, setNumberInput] = useState('10, 2, 38, 23, 38, 23, 21');
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const parseNumbers = (input: string): number[] => {
    const numbers = input
      .split(/[\s,;]+/)
      .map(s => s.trim())
      .filter(s => s.length > 0)
      .map(s => parseFloat(s))
      .filter(n => !isNaN(n));

    return numbers;
  };

  const calculateMean = (numbers: number[]): number => {
    const sum = numbers.reduce((acc, num) => acc + num, 0);
    return sum / numbers.length;
  };

  const calculateMedian = (sortedNumbers: number[]): number => {
    const len = sortedNumbers.length;
    const mid = Math.floor(len / 2);

    if (len % 2 === 0) {
      return (sortedNumbers[mid - 1] + sortedNumbers[mid]) / 2;
    } else {
      return sortedNumbers[mid];
    }
  };

  const calculateMode = (numbers: number[]): string => {
    const frequency: { [key: number]: number } = {};
    let maxFreq = 0;

    numbers.forEach(num => {
      frequency[num] = (frequency[num] || 0) + 1;
      if (frequency[num] > maxFreq) {
        maxFreq = frequency[num];
      }
    });

    if (maxFreq === 1) {
      return t('noMode');
    }

    const modes = Object.keys(frequency)
      .filter(key => frequency[parseFloat(key)] === maxFreq)
      .map(key => parseFloat(key))
      .sort((a, b) => a - b);

    return modes.join(', ');
  };

  const calculateRange = (numbers: number[]): number => {
    const min = Math.min(...numbers);
    const max = Math.max(...numbers);
    return max - min;
  };

  const formatNumber = (num: number): string => {
    if (Number.isInteger(num)) {
      return num.toString();
    }
    return num.toFixed(3).replace(/\.?0+$/, '');
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const input = numberInput.trim();

    if (!input) {
      setError(t('errorEmpty'));
      setResults(null);
      return;
    }

    const numbers = parseNumbers(input);

    if (numbers.length === 0) {
      setError(t('errorInvalid'));
      setResults(null);
      return;
    }

    const sortedNumbers = [...numbers].sort((a, b) => a - b);

    const mean = calculateMean(numbers);
    const median = calculateMedian(sortedNumbers);
    const mode = calculateMode(numbers);
    const range = calculateRange(numbers);
    const sum = numbers.reduce((acc, num) => acc + num, 0);
    const min = Math.min(...numbers);
    const max = Math.max(...numbers);

    setResults({
      mean: formatNumber(mean),
      median: formatNumber(median),
      mode,
      range: formatNumber(range),
      count: numbers.length,
      sum: formatNumber(sum),
      min: formatNumber(min),
      max: formatNumber(max),
      sorted: sortedNumbers.map(formatNumber).join(', ')
    });
  };

  const handleClear = () => {
    setNumberInput('');
    setResults(null);
    setError('');
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, margin: '0 auto' },
    { fontSize: '1rem', maxWidth: '100%' },
    { fontSize: '1.125rem', maxWidth: '700px' },
    { fontSize: '1.25rem', maxWidth: '800px' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto', background: 'white', borderRadius: '1rem', overflow: 'hidden' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const formGroupStyle = {
    marginBottom: '1.5rem'
  };

  const labelStyle = getResponsiveStyle(
    { display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: colors.textPrimary },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const textareaStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontFamily: "'Courier New', monospace",
      transition: 'all 0.3s ease',
      resize: 'vertical' as const,
      minHeight: '120px',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem', fontSize: '0.95rem' },
    { padding: '0.875rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1.05rem' }
  );

  const inputHintStyle = getResponsiveStyle(
    { color: '#64748b', marginTop: '0.5rem', display: 'block' },
    { fontSize: '0.8rem' },
    { fontSize: '0.85rem' },
    { fontSize: '0.875rem' }
  );

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginTop: '2rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      flex: 1,
      fontWeight: 600,
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.05rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' }
  );

  const clearButtonStyle = getResponsiveStyle(
    {
      flex: 1,
      fontWeight: 600,
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.05rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' }
  );

  const errorMessageStyle = getResponsiveStyle(
    {
      background: '#fee2e2',
      color: '#991b1b',
      borderRadius: '0.5rem',
      marginTop: '1rem',
      borderLeft: '4px solid #dc2626'
    },
    { padding: '0.75rem', fontSize: '0.9rem' },
    { padding: '0.875rem', fontSize: '0.95rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderTop: '3px solid #f59e0b'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: '#92400e', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.3rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const statsGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(4, 1fr)' }
  );

  const statCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
      borderLeft: '4px solid #2563eb'
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const statLabelStyle = getResponsiveStyle(
    { color: '#64748b', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em', marginBottom: '0.5rem' },
    { fontSize: '0.75rem' },
    { fontSize: '0.8rem' },
    { fontSize: '0.875rem' }
  );

  const statValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const additionalStatsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const additionalStatsH4Style = getResponsiveStyle(
    { margin: '0 0 1rem 0', color: colors.textPrimary },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const statRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.75rem 0',
    borderBottom: '1px solid #f1f5f9'
  };

  const statRowLabelStyle = getResponsiveStyle(
    { color: '#64748b', fontWeight: 500 },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const statRowValueStyle = getResponsiveStyle(
    { color: colors.textPrimary, fontWeight: 600 },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const sortedDataStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
      marginTop: '1.5rem'
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const sortedValuesStyle = getResponsiveStyle(
    { fontFamily: "'Courier New', monospace", color: '#475569', lineHeight: 1.8, wordWrap: 'break-word' as const },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const listItemStyle = {
    marginBottom: '0.75rem'
  };

  const strongStyle = {
    color: colors.textPrimary,
    fontWeight: 600
  };

  const faqItemStyle = getResponsiveStyle(
    { marginBottom: '1.5rem' },
    {},
    { marginBottom: '1.75rem' },
    { marginBottom: '2rem' }
  );

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.15rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.7 },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' }
  );

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    transition: 'color 0.3s ease'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>📊 {t('toolTitle')}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label htmlFor="numberInput" style={labelStyle}>
                {t('inputLabel')}
              </label>
              <textarea
                id="numberInput"
                style={textareaStyle}
                placeholder={t('inputPlaceholder')}
                value={numberInput}
                onChange={(e) => setNumberInput(e.target.value)}
                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
              <span style={inputHintStyle}>{t('inputHint')}</span>
            </div>

            <div style={buttonGroupStyle}>
              <button
                type="submit"
                style={calculateButtonStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 10px 20px rgba(37, 99, 235, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {t('calculateButton')}
              </button>
              <button
                type="button"
                style={clearButtonStyle}
                onClick={handleClear}
                onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#f1f5f9'}
              >
                {t('clearButton')}
              </button>
            </div>

            {error && (
              <div style={errorMessageStyle}>{error}</div>
            )}
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>📈 {t('resultsTitle')}</h3>

              <div style={statsGridStyle}>
                <div style={statCardStyle}>
                  <div style={statLabelStyle}>{t('meanLabel')}</div>
                  <div style={statValueStyle}>{results.mean}</div>
                </div>

                <div style={{ ...statCardStyle, borderLeftColor: '#7c3aed' }}>
                  <div style={statLabelStyle}>{t('medianLabel')}</div>
                  <div style={statValueStyle}>{results.median}</div>
                </div>

                <div style={{ ...statCardStyle, borderLeftColor: '#f59e0b' }}>
                  <div style={statLabelStyle}>{t('modeLabel')}</div>
                  <div style={statValueStyle}>{results.mode}</div>
                </div>

                <div style={{ ...statCardStyle, borderLeftColor: '#10b981' }}>
                  <div style={statLabelStyle}>{t('rangeLabel')}</div>
                  <div style={statValueStyle}>{results.range}</div>
                </div>
              </div>

              <div style={additionalStatsStyle}>
                <h4 style={additionalStatsH4Style}>{t('additionalStatsTitle')}</h4>
                <div style={statRowStyle}>
                  <span style={statRowLabelStyle}>{t('countLabel')}</span>
                  <span style={statRowValueStyle}>{results.count}</span>
                </div>
                <div style={statRowStyle}>
                  <span style={statRowLabelStyle}>{t('sumLabel')}</span>
                  <span style={statRowValueStyle}>{results.sum}</span>
                </div>
                <div style={statRowStyle}>
                  <span style={statRowLabelStyle}>{t('minLabel')}</span>
                  <span style={statRowValueStyle}>{results.min}</span>
                </div>
                <div style={{ ...statRowStyle, borderBottom: 'none' }}>
                  <span style={statRowLabelStyle}>{t('maxLabel')}</span>
                  <span style={statRowValueStyle}>{results.max}</span>
                </div>
              </div>

              <div style={sortedDataStyle}>
                <h4 style={additionalStatsH4Style}>{t('sortedDataTitle')}</h4>
                <div style={sortedValuesStyle}>{results.sorted}</div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>📖 {t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>🎯 {t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Description')}
          </li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>💡 {t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('insightMeanTitle')}</strong> {t('insightMeanDescription')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('insightMedianTitle')}</strong> {t('insightMedianDescription')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('insightModeTitle')}</strong> {t('insightModeDescription')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('insightRangeTitle')}</strong> {t('insightRangeDescription')}
          </li>
        </ul>
        <p style={paragraphStyle}>{t('insightsParagraph2')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>📊 {t('detailsTitle')}</h2>
        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.2rem' : screenSize === 'tablet' ? '1.35rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('detailsMeanTitle')}
        </h3>
        <p style={paragraphStyle}>{t('detailsMeanDescription')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.2rem' : screenSize === 'tablet' ? '1.35rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('detailsMedianTitle')}
        </h3>
        <p style={paragraphStyle}>{t('detailsMedianDescription')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.2rem' : screenSize === 'tablet' ? '1.35rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('detailsModeTitle')}
        </h3>
        <p style={paragraphStyle}>{t('detailsModeDescription')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.2rem' : screenSize === 'tablet' ? '1.35rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('detailsRangeTitle')}
        </h3>
        <p style={paragraphStyle}>{t('detailsRangeDescription')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>❓ {t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq1Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq2Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq3Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq4Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq5Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq6Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq6Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>📚 {t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <a
              href="https://www.easycalculation.com/statistics/mean-median-mode.php"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('ref1')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://www.calculators.org/math/mean-median-mode.php"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('ref2')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://www.inchcalculator.com/mean-median-mode-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('ref3')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://www.calculatorsoup.com/calculators/statistics/mean-median-mode.php"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('ref4')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://www.calculator.net/mean-median-mode-range-calculator.html"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('ref5')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}